import { Component, Input, OnChanges, SimpleChanges, ViewEncapsulation, OnDestroy } from '@angular/core';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { finalize } from 'rxjs/operators/finalize';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs/Subject';
import { takeUntil, first } from 'rxjs/operators';
import { DateUtils } from '../../../../utilities/dateUtils';
import { GetTelerikReportModel } from '../../../../telerik/models/getTelerikReport.model';
import { CoreReportDTO, CoreReportVersionDTO } from '../../../../telerik/models/coreReport.model';
import { TelerikReportService } from '../../../../telerik/services/telerikReport.service';

@Component({
  selector: 'app-report-preview',
  templateUrl: './report-preview.component.html',
  styleUrls: ['./report-preview.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportPreviewComponent implements OnChanges, OnDestroy {
  @Input() reportModel: GetTelerikReportModel;
  @Input() selectedVersion: CoreReportVersionDTO;

  reportHistory: CoreReportDTO;
  previewUrl: SafeResourceUrl;
  previewBlob: Blob;
  previewBase64: string;
  loading = false;
  error: string;
  private blobUrl: string;
  private destroy$ = new Subject<void>();
  private currentFileReader: FileReader | null = null;

  isDownloading = false;
  isPrinting = false;

  constructor(
    private telerikReportService: TelerikReportService,
    private sanitizer: DomSanitizer,
    private toastrService: ToastrService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.reportModel && this.reportModel) {
      this.loadReportHistory();
    }

    if (changes.selectedVersion && this.selectedVersion) {
      this.previewVersion(this.selectedVersion);
    }
  }

  loadReportHistory(): void {
    this.loading = true;
    this.error = null;

    this.telerikReportService.getReportHistory(this.reportModel.fileName)
      .pipe(
        first(),
        finalize(() => this.loading = false),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (history) => {
          this.reportHistory = history;
        },
        (err) => {
          this.error = 'Failed to load report history. Please try again.';
          console.error('Error loading report history:', err);
        }
      );
  }

  async previewVersion(version: CoreReportVersionDTO): Promise<void> {
    if (!version) { return; }

    this.loading = true;
    this.error = null;
    this.previewBase64 = null;
    this.previewUrl = null;
    this.pdfSrc = null;

    try {
      const response = await this.telerikReportService.getReportBlob(version.reportId, version.id);
      this.previewBlob = response.blob;

      this.convertBlobToFile(this.previewBlob);
    } catch(err) {
      this.error = 'Failed to load preview. Please try again.';
      console.error('Error loading preview:', err);
      this.previewUrl = null;
      this.previewBase64 = null;
      this.pdfSrc = null;
    }
  }

  onPdfError(error: any): void {
    console.error('PDF viewer error:', error);
    this.error = 'Failed to display PDF preview. Please try downloading the file instead.';
    this.previewBase64 = null;
    this.previewUrl = null;
  }

  async downloadVersion(version: CoreReportVersionDTO, event): Promise<void> {
    event.stopPropagation();
    this.isDownloading = true;

    try {
      await this.telerikReportService.downloadReportVersion(version.reportId, version.id);
    } catch (error) {
      console.error('Error downloading report:', error);
      this.toastrService.error('Error downloading report');
    } finally {
      this.isDownloading = false;
    }
  }

  async printVersion(version: CoreReportVersionDTO, event): Promise<void> {
    event.stopPropagation();
    this.isPrinting = true;

    try {
      await this.telerikReportService.printReportVersion(version.reportId, version.id);
    } catch (error) {
      console.error('Error downloading report:', error);
      this.toastrService.error('Error downloading report');
    } finally {
      this.isPrinting = false;
    }
  }

  getReportDate(date: Date): string {
    return DateUtils.getUtcDate(date).toLocaleString();
  }

  onPrintClick(version: CoreReportVersionDTO, event: Event): void {
    this.printVersion(version, event);
  }

  pdfSrc: Uint8Array;
  convertBlobToFile(blob: Blob) {
    // Clean up previous FileReader if it exists
    if (this.currentFileReader) {
      this.currentFileReader.abort();
      this.currentFileReader = null;
    }

    this.currentFileReader = new FileReader();
    this.currentFileReader.onload = () => {
      this.pdfSrc = new Uint8Array(this.currentFileReader.result as ArrayBuffer);
      this.loading = false;
      this.currentFileReader = null;
    };

    this.currentFileReader.onerror = () => {
      this.error = 'Failed to read PDF data. Please try again.';
      this.loading = false;
      console.error('Error reading PDF blob');
      this.currentFileReader = null;
    };

    this.currentFileReader.readAsArrayBuffer(blob);
  }

  ngOnDestroy(): void {
    if (this.currentFileReader) {
      this.currentFileReader.abort();
      this.currentFileReader = null;
    }

    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }

    this.previewBlob = null;
    this.previewBase64 = null;
    this.pdfSrc = null;
    this.previewUrl = null;
    this.reportHistory = null;


    this.destroy$.next();
    this.destroy$.complete();
  }
}


// WEBPACK FOOTER //
// ./src/app/core/components/report-generator/report-generator-content/report-preview/report-preview.component.ts